import { createSignal, createEffect, createMemo, For, Show, untrack, onMount, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { createStore, reconcile } from 'solid-js/store';
import { A } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import SellbotPausedBadge from '../components/SellbotPausedBadge';
import { DateRangePills } from '../components/DateRangePills';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { showConfirm, showDialog } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { formatQuantity } from '../services/quantityFormatter';
import { resolveItemDisplayName } from '../services/itemDisplayName';
import { formatMinecraftText } from '../components/MinecraftText';
import { parseSnbt } from '../services/snbtReader';
import { createSellbotNameCache, searchSellbotItems } from '../services/sellbotItemSearch';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { debounce } from '../services/debounceCallback';
import { useDebounced } from '../hooks/useDebounced';
import ItemStackView from '../components/ItemStackView';
import Modal from '../components/Modal';
import { BulkActionBar } from '../components/BulkActionBar';
import ItemIdentityWizard, { type WizardItemSource } from '../components/ItemIdentityWizard';
import { downloadFromApi } from '../services/downloadFile';
import { getSellbotExportOffersUrl } from '../api/generated/sellbot';
import type { SellbotConfigDto, SellbotOfferDto, SellbotOfferFolderDto, ItemStackDto, InventoryCustomItemDto, SellbotOfferImportResultDto, SellbotWizardRequest } from '../api';
import styles from './Sellbot.module.css';

type OfferFilter = 'all' | 'sell' | 'ankauf' | 'oos' | 'rules';
type TreeItemRef = { itemType: 'Offer' | 'Folder'; itemId: string };

// Kept outside the component so search/filter/scroll survive the unmount that happens when
// navigating to an offer detail page and back.
const [persistedSearch, setPersistedSearch] = createSignal('');
const [persistedFilter, setPersistedFilter] = createSignal<OfferFilter>('all');
const [persistedFolderId, setPersistedFolderId] = createSignal<string | null>(null);
const [persistedSelectedOfferIds, setPersistedSelectedOfferIds] = createSignal<Set<string>>(new Set());
let persistedScrollY = 0;

export default function SellbotOffers() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();
    const { botId } = useActiveBot();

    const displayName = (item: ItemStackDto): string =>
        resolveItemDisplayName({ itemId: item.itemId, damage: item.metadata, snbt: item.snbt ?? undefined }, resolver());

    const coloredDisplayName = (item: ItemStackDto) =>
        formatMinecraftText(parseSnbt(item.snbt ?? undefined).displayName ?? displayName(item));

    const fmtMoney = (n: number) => `${new Intl.NumberFormat('de-DE').format(n)}$`;

    const minTierCount = (o: SellbotOfferDto): number => {
        const enabled = o.tiers.filter(tr => tr.isEnabled).map(tr => tr.count);
        return enabled.length ? Math.min(...enabled) : 0;
    };

    const releasedCount = (o: SellbotOfferDto): number =>
        o.dimensions.filter(dimension => dimension.state === 'Ignored').length;

    const isOutOfStock = (o: SellbotOfferDto): boolean => {
        if (o.ignoresStockCache) return false;
        const min = minTierCount(o);
        return min > 0 && o.availableStock < min;
    };

    const stockClass = (o: SellbotOfferDto): string => {
        if (o.ignoresStockCache) return '';
        const min = minTierCount(o);
        if (min === 0) return '';
        if (o.availableStock < min) return styles.stockLow;
        if (o.availableStock < min * 3) return styles.stockMid;
        return '';
    };

    const offerStackSize = (o: SellbotOfferDto) => resolver()?.getItemById(o.item.itemId, o.item.metadata)?.stackSize;

    const tierSummary = (o: SellbotOfferDto): string => {
        if (o.tiers.length === 0) return t('SELLBOT_NO_TIERS');
        const prices = o.tiers.map(x => x.price).sort((a, b) => a - b);
        if (o.tiers.length === 1) return `×${formatQuantity(o.tiers[0].count, offerStackSize(o))} · ${fmtMoney(prices[0])}`;
        return `${o.tiers.length}× · ${fmtMoney(prices[0])}-${fmtMoney(prices[prices.length - 1])}`;
    };

    // One per bundled price: an item priced 5000 and 7000 can be in a different bundle at each.
    const bundlesOf = (o: SellbotOfferDto) =>
        (config()?.bundles ?? []).filter(bundle => bundle.members.some(member => member.offerId === o.id));

    const hasVerkaufLimit = (o: SellbotOfferDto): boolean => o.verkaufLimits.some(l => l.isEnabled);
    const hasAnkaufLimit = (o: SellbotOfferDto): boolean => o.ankaufLimits.some(l => l.isEnabled);

    const hasPausedTier = (o: SellbotOfferDto): boolean =>
        o.tiers.some(tr => !tr.isEnabled);

    const hasPausedAnkaufStage = (o: SellbotOfferDto): boolean =>
        o.ankaufTier ? !o.ankaufTier.isEnabled || o.ankaufTier.stages.some(stage => !stage.isEnabled) : false;

    const ankaufSummary = (o: SellbotOfferDto): string => {
        const stages = o.ankaufTier?.stages ?? [];
        if (stages.length === 0) return t('SELLBOT_ANKAUF_NO_STAGES');
        const prices = stages.map(x => x.price).sort((a, b) => a - b);
        if (stages.length === 1) return `×${formatQuantity(stages[0].count, offerStackSize(o))} · ${fmtMoney(prices[0])}`;
        return `${stages.length}× · ${fmtMoney(prices[0])}-${fmtMoney(prices[prices.length - 1])}`;
    };

    const [configStore, setConfigStore] = createStore<{ config: SellbotConfigDto | null }>({ config: null });
    const config = () => configStore.config;
    const setConfig = (v: SellbotConfigDto | null) => setConfigStore('config', v ? reconcile(v, { key: 'id' }) : null);
    const [loading, setLoading] = createSignal(true);
    const [busyScan, setBusyScan] = createSignal(false);
    const [stock, setStock] = createStore<ItemStackDto[]>([]);
    const [stockQuantities, setStockQuantities] = createStore<Record<string, number>>({});
    const [customItems, setCustomItems] = createStore<InventoryCustomItemDto[]>([]);
    const [wizardOpen, setWizardOpen] = createSignal(false);

    const [search, setSearch] = createSignal(persistedSearch());
    const [filter, setFilter] = createSignal<OfferFilter>(persistedFilter());
    const [currentFolderId, setCurrentFolderId] = createSignal<string | null>(persistedFolderId());
    createEffect(() => setPersistedSearch(search()));
    createEffect(() => setPersistedFilter(filter()));
    createEffect(() => setPersistedFolderId(currentFolderId()));

    const filteredOffers = createMemo(() => {
        const q = search().trim().toLowerCase();
        const f = filter();
        return (config()?.offers ?? []).filter(o => {
            if (q && !displayName(o.item).toLowerCase().includes(q)
                && !o.item.key.toLowerCase().includes(q)
                && !(resolver()?.matchesSearch(o.item.key, q) ?? false)) return false;
            if (f === 'sell' && o.tiers.length === 0) return false;
            if (f === 'ankauf' && !o.ankaufTier) return false;
            if (f === 'oos' && !isOutOfStock(o)) return false;
            if (f === 'rules' && releasedCount(o) === 0) return false;
            return true;
        });
    });

    const isSearching = createMemo(() => search().trim().length > 0 || filter() !== 'all');

    const breadcrumb = createMemo(() => {
        const folders = config()?.folders ?? [];
        const trail: SellbotOfferFolderDto[] = [];
        let cursor = currentFolderId();
        while (cursor) {
            const folder = folders.find(f => f.id === cursor);
            if (!folder) break;
            trail.unshift(folder);
            cursor = folder.parentFolderId ?? null;
        }
        return trail;
    });

    const visibleFolders = createMemo(() => (config()?.folders ?? [])
        .filter(f => (f.parentFolderId ?? null) === currentFolderId())
        .sort((a, b) => a.sortOrder - b.sortOrder));

    const visibleOffers = createMemo(() => (config()?.offers ?? [])
        .filter(o => (o.folderId ?? null) === currentFolderId())
        .sort((a, b) => a.sortOrder - b.sortOrder));

    // ---- Drag and drop -----------------------------------------------------------------------
    // Pointer-events driven, not native HTML5 drag-and-drop. Native DnD's dragover hit-testing
    // targets whatever DOM element is currently under the cursor, which breaks the moment that
    // element is the thing being animated by the "make space" preview: shifting a row out from
    // under a stationary cursor makes the browser immediately re-fire dragover on whatever is now
    // underneath, cascading past the intended target. Resolving the hover target from captured
    // geometry instead of live hit-testing removes that failure mode at the root instead of
    // working around one symptom of it at a time.
    type TreeRowType = 'Offer' | 'Folder';
    type DragMode =
        | { kind: 'reorder'; index: number }
        | { kind: 'enter-folder'; folderId: string }
        | { kind: 'up' }
        | { kind: 'crumb'; folderId: string | null }
        | null;
    type DragState = {
        type: TreeRowType;
        id: string;
        icon: string;
        label: string;
        fromIndex: number;
        rowHeight: number;
        pointerX: number;
        pointerY: number;
        grabOffsetX: number;
        grabOffsetY: number;
        blockedFolderIds: Set<string>;
        mode: DragMode;
    };

    const [drag, setDrag] = createSignal<DragState | null>(null);

    const descendantFolderIds = (folderId: string): Set<string> => {
        const ids = new Set<string>();
        const collect = (id: string) => {
            for (const f of config()?.folders ?? []) {
                if (f.parentFolderId === id) { ids.add(f.id); collect(f.id); }
            }
        };
        collect(folderId);
        return ids;
    };

    let rowGeometry: { id: string; type: TreeRowType; top: number; height: number }[] = [];
    let crumbGeometry: { folderId: string | null; rect: DOMRect }[] = [];
    let upRowRect: DOMRect | null = null;

    const resolveDragMode = (clientX: number, clientY: number, active: DragState): DragMode => {
        for (const c of crumbGeometry) {
            if (clientX >= c.rect.left && clientX <= c.rect.right && clientY >= c.rect.top && clientY <= c.rect.bottom) {
                if (active.type === 'Folder' && active.id === c.folderId) return null;
                return { kind: 'crumb', folderId: c.folderId };
            }
        }
        if (upRowRect && clientY >= upRowRect.top && clientY <= upRowRect.bottom) {
            return { kind: 'up' };
        }
        if (rowGeometry.length === 0) return { kind: 'reorder', index: 0 };
        const docY = clientY + window.scrollY;
        for (let i = 0; i < rowGeometry.length; i++) {
            const r = rowGeometry[i];
            if (docY < r.top || docY > r.top + r.height) continue;
            const fraction = (docY - r.top) / r.height;
            if (r.type === 'Folder' && r.id !== active.id && !active.blockedFolderIds.has(r.id) && fraction > 0.25 && fraction < 0.75) {
                return { kind: 'enter-folder', folderId: r.id };
            }
            return { kind: 'reorder', index: fraction < 0.5 ? i : i + 1 };
        }
        const last = rowGeometry[rowGeometry.length - 1];
        return { kind: 'reorder', index: docY > last.top + last.height ? rowGeometry.length : 0 };
    };

    // Rows strictly between the dragged item's original slot and the position it would be
    // inserted at shift by the dragged row's own height, closing the gap it left and opening one
    // where it would land - sized by the dragged item, not by each shifted row's own height, so
    // this stays correct even when rows have different heights (an offer's note/badges wrap).
    const rowShift = (index: number): number => {
        const d = drag();
        if (!d || d.mode?.kind !== 'reorder') return 0;
        const from = d.fromIndex;
        const over = d.mode.index;
        if (from < 0) return 0;
        if (over > from && index > from && index < over) return -d.rowHeight;
        if (over <= from && index >= over && index < from) return d.rowHeight;
        return 0;
    };

    const isCrumbDropTarget = (folderId: string | null) => {
        const mode = drag()?.mode;
        return mode?.kind === 'crumb' && mode.folderId === folderId;
    };

    const isFolderEnterTarget = (folderId: string) => {
        const mode = drag()?.mode;
        return mode?.kind === 'enter-folder' && mode.folderId === folderId;
    };

    const DRAG_SCROLL_ZONE_PX = 96;
    const [dragScrollZone, setDragScrollZone] = createSignal<'top' | 'bottom' | null>(null);
    let dragScrollRaf: number | null = null;

    const dragScrollTick = () => {
        const pointerY = drag()?.pointerY ?? null;
        let zone: 'top' | 'bottom' | null = null;
        if (pointerY !== null) {
            if (pointerY < DRAG_SCROLL_ZONE_PX) zone = 'top';
            else if (pointerY > window.innerHeight - DRAG_SCROLL_ZONE_PX) zone = 'bottom';
        }
        if (zone !== dragScrollZone()) setDragScrollZone(zone);
        if (zone === 'top') window.scrollBy(0, -Math.max(3, (DRAG_SCROLL_ZONE_PX - (pointerY ?? 0)) * 0.4));
        else if (zone === 'bottom') window.scrollBy(0, Math.max(3, ((pointerY ?? 0) - (window.innerHeight - DRAG_SCROLL_ZONE_PX)) * 0.4));
        dragScrollRaf = requestAnimationFrame(dragScrollTick);
    };

    const onDragPointerMove = (e: PointerEvent) => {
        const d = drag();
        if (!d) return;
        const mode = resolveDragMode(e.clientX, e.clientY, d);
        setDrag({ ...d, pointerX: e.clientX, pointerY: e.clientY, mode });
    };

    const onDragKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') endDrag(null);
    };

    const endDrag = (finalState: DragState | null) => {
        window.removeEventListener('pointermove', onDragPointerMove);
        window.removeEventListener('pointerup', onDragPointerUp);
        window.removeEventListener('keydown', onDragKeyDown);
        if (dragScrollRaf !== null) { cancelAnimationFrame(dragScrollRaf); dragScrollRaf = null; }
        setDragScrollZone(null);
        setDrag(null);

        const mode = finalState?.mode;
        let reorderCall: (() => void) | null = null;
        if (mode?.kind === 'reorder') {
            // Pure reorder within the current level: splice the dragged item out of the geometry
            // snapshot taken at drag start and back in at the resolved insertion point, then hand
            // the whole resulting order to moveTreeItem as a forced order rather than an
            // append/target pair - the insertion index already encodes exactly where it goes.
            // Must read rowGeometry before it's cleared below.
            const order = rowGeometry.map(r => ({ itemType: r.type, itemId: r.id }));
            const fromIdx = order.findIndex(r => r.itemType === finalState!.type && r.itemId === finalState!.id);
            if (fromIdx >= 0) {
                const [moved] = order.splice(fromIdx, 1);
                const insertAt = mode.index > fromIdx ? mode.index - 1 : mode.index;
                order.splice(insertAt, 0, moved);
                reorderCall = () => void moveTreeItem(finalState!.type, finalState!.id, currentFolderId(), false, { forcedOrder: order });
            }
        }

        rowGeometry = [];
        crumbGeometry = [];
        upRowRect = null;

        if (!finalState || !mode) return;
        if (mode.kind === 'up') { void moveTreeItem(finalState.type, finalState.id, parentFolderId(), true); return; }
        if (mode.kind === 'crumb') { void moveTreeItem(finalState.type, finalState.id, mode.folderId, true); return; }
        if (mode.kind === 'enter-folder') { void moveTreeItem(finalState.type, finalState.id, mode.folderId, true); return; }
        reorderCall?.();
    };

    const onDragPointerUp = () => endDrag(drag());

    const beginDrag = (type: TreeRowType, id: string, label: string, icon: string) => (e: PointerEvent) => {
        if (e.button !== 0) return;
        const row = (e.currentTarget as HTMLElement).closest('tr');
        if (!row) return;
        e.preventDefault();
        const rect = row.getBoundingClientRect();

        rowGeometry = Array.from(document.querySelectorAll<HTMLElement>('[data-tree-row-id]')).map(el => {
            const r = el.getBoundingClientRect();
            return { id: el.dataset.treeRowId!, type: el.dataset.treeRowType as TreeRowType, top: r.top + window.scrollY, height: r.height };
        });
        crumbGeometry = Array.from(document.querySelectorAll<HTMLElement>('[data-crumb-id]')).map(el => ({
            folderId: el.dataset.crumbId === 'root' ? null : el.dataset.crumbId!,
            rect: el.getBoundingClientRect(),
        }));
        const upEl = document.querySelector<HTMLElement>('[data-uprow]');
        upRowRect = upEl ? upEl.getBoundingClientRect() : null;

        const fromIndex = rowGeometry.findIndex(r => r.id === id && r.type === type);

        setDrag({
            type, id, label, icon,
            fromIndex,
            rowHeight: rect.height,
            pointerX: e.clientX, pointerY: e.clientY,
            grabOffsetX: e.clientX - rect.left, grabOffsetY: e.clientY - rect.top,
            blockedFolderIds: type === 'Folder' ? new Set([id, ...descendantFolderIds(id)]) : new Set(),
            mode: fromIndex >= 0 ? { kind: 'reorder', index: fromIndex } : null,
        });

        window.addEventListener('pointermove', onDragPointerMove);
        window.addEventListener('pointerup', onDragPointerUp);
        window.addEventListener('keydown', onDragKeyDown);
        dragScrollRaf = requestAnimationFrame(dragScrollTick);
    };

    const parentFolderId = createMemo(() => {
        const folder = (config()?.folders ?? []).find(f => f.id === currentFolderId());
        return folder?.parentFolderId ?? null;
    });

    // Applied instantly to the store so a row snaps to its new spot without waiting on the round
    // trip; the API call below just persists what's already on screen.
    const applyLocalOrder = (orderedRefs: TreeItemRef[]) => {
        orderedRefs.forEach((ref, index) => {
            if (ref.itemType === 'Offer') setConfigStore('config', 'offers', o => o.id === ref.itemId, 'sortOrder', index);
            else setConfigStore('config', 'folders', f => f.id === ref.itemId, 'sortOrder', index);
        });
    };

    const applyLocalFolder = (draggedType: 'Offer' | 'Folder', draggedId: string, destinationFolderId: string | null) => {
        if (draggedType === 'Offer') setConfigStore('config', 'offers', o => o.id === draggedId, 'folderId', destinationFolderId);
        else setConfigStore('config', 'folders', f => f.id === draggedId, 'parentFolderId', destinationFolderId);
    };

    const siblingRefs = (destinationFolderId: string | null): TreeItemRef[] => [
        ...(config()?.folders ?? []).filter(f => (f.parentFolderId ?? null) === destinationFolderId)
            .map(f => ({ itemType: 'Folder' as const, itemId: f.id })),
        ...(config()?.offers ?? []).filter(o => (o.folderId ?? null) === destinationFolderId)
            .map(o => ({ itemType: 'Offer' as const, itemId: o.id })),
    ];

    const folderName = (folderId: string | null) =>
        folderId === null ? t('SELLBOT_BREADCRUMB_ROOT') : (config()?.folders ?? []).find(f => f.id === folderId)?.name ?? '';

    const moveTreeItem = async (draggedType: 'Offer' | 'Folder', draggedId: string, destinationFolderId: string | null,
        append: boolean, opts?: { silent?: boolean; forcedOrder?: TreeItemRef[] }) => {
        const id = botId();
        if (!id) return;
        if (draggedType === 'Folder' && draggedId === destinationFolderId) return;

        const originalFolderId = draggedType === 'Offer'
            ? (config()?.offers ?? []).find(o => o.id === draggedId)?.folderId ?? null
            : (config()?.folders ?? []).find(f => f.id === draggedId)?.parentFolderId ?? null;
        const originalSiblings = siblingRefs(originalFolderId);

        let siblings: TreeItemRef[];
        if (opts?.forcedOrder) {
            siblings = opts.forcedOrder;
        } else {
            siblings = siblingRefs(destinationFolderId).filter(s => !(s.itemType === draggedType && s.itemId === draggedId));
            if (append) siblings = [...siblings, { itemType: draggedType, itemId: draggedId }];
        }

        applyLocalFolder(draggedType, draggedId, destinationFolderId);
        applyLocalOrder(siblings);

        const res = await api.sellbot.moveOfferTreeItem(id, {
            itemType: draggedType,
            itemId: draggedId,
            targetFolderId: destinationFolderId,
            targetOrder: siblings,
        });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_REORDER_FAILED'));
            await loadAll();
            return;
        }

        if (!opts?.silent && originalFolderId !== destinationFolderId) {
            toast?.info(t('SELLBOT_TOAST_MOVED', folderName(destinationFolderId)), {
                actionLabel: t('COMMON_UNDO'),
                onAction: () => void moveTreeItem(draggedType, draggedId, originalFolderId, true, { silent: true, forcedOrder: originalSiblings }),
            });
        }
    };

    const allFoldersFlat = createMemo(() => {
        const byParent = new Map<string | null, SellbotOfferFolderDto[]>();
        for (const f of config()?.folders ?? []) {
            const key = f.parentFolderId ?? null;
            if (!byParent.has(key)) byParent.set(key, []);
            byParent.get(key)!.push(f);
        }
        for (const list of byParent.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
        const out: { folder: SellbotOfferFolderDto; depth: number }[] = [];
        const walk = (parentId: string | null, depth: number) => {
            for (const f of byParent.get(parentId) ?? []) {
                out.push({ folder: f, depth });
                walk(f.id, depth + 1);
            }
        };
        walk(null, 0);
        return out;
    });

    const [contextMenu, setContextMenu] = createSignal<{ x: number; y: number; itemType: 'Offer' | 'Folder'; itemId: string } | null>(null);

    const openContextMenu = (itemType: 'Offer' | 'Folder', itemId: string) => (e: MouseEvent) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, itemType, itemId });
    };

    let contextMenuRef: HTMLDivElement | undefined;

    // Shared by every floating menu here (right-click context menu, bulk "move to"): opens at the
    // trigger point first (no flash of a wrong position), then nudges back inside the viewport
    // once its real size is known - it would otherwise render partly off-screen near an edge with
    // no way to reach its lower items.
    const useClampToViewport = (ref: () => HTMLElement | undefined, pos: () => { x: number; y: number } | null) => {
        createEffect(() => {
            const p = pos();
            const el = ref();
            if (!p || !el) return;
            requestAnimationFrame(() => {
                const rect = el.getBoundingClientRect();
                const clampedX = Math.max(8, Math.min(p.x, window.innerWidth - rect.width - 8));
                const clampedY = Math.max(8, Math.min(p.y, window.innerHeight - rect.height - 8));
                el.style.left = `${clampedX}px`;
                el.style.top = `${clampedY}px`;
            });
        });
    };

    // Deferred: the click that opened one of these menus can otherwise be seen by a same-tick
    // document click/keydown listener and close the menu before it ever renders.
    const useDismissableMenu = (isOpen: () => boolean, close: () => void) => {
        createEffect(() => {
            if (!isOpen()) return;
            const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
            const timer = setTimeout(() => {
                document.addEventListener('click', close);
                document.addEventListener('keydown', onKey);
            }, 0);
            onCleanup(() => {
                clearTimeout(timer);
                document.removeEventListener('click', close);
                document.removeEventListener('keydown', onKey);
            });
        });
    };

    useClampToViewport(() => contextMenuRef, () => contextMenu());
    useDismissableMenu(() => contextMenu() !== null, () => setContextMenu(null));

    const moveTargetsFor = (itemType: 'Offer' | 'Folder', itemId: string) => {
        const currentParent = itemType === 'Offer'
            ? (config()?.offers ?? []).find(o => o.id === itemId)?.folderId ?? null
            : (config()?.folders ?? []).find(f => f.id === itemId)?.parentFolderId ?? null;
        const excluded = itemType === 'Folder' ? new Set([itemId, ...descendantFolderIds(itemId)]) : new Set<string>();
        return {
            root: currentParent !== null,
            folders: allFoldersFlat().filter(({ folder }) => !excluded.has(folder.id) && folder.id !== currentParent),
        };
    };

    const moveViaMenu = (itemType: 'Offer' | 'Folder', itemId: string, destinationFolderId: string | null) => {
        setContextMenu(null);
        void moveTreeItem(itemType, itemId, destinationFolderId, true);
    };

    // Bulk selection covers offers only - folders keep their own move/rename/delete affordances,
    // and mixing folder trees into a bulk operation raises the same descendant-exclusion questions
    // moveTargetsFor already handles per-folder, for no clear benefit here.
    const currentOfferList = createMemo(() => isSearching() ? filteredOffers() : visibleOffers());
    const [selectedOfferIds, setSelectedOfferIds] = createSignal<Set<string>>(persistedSelectedOfferIds());
    createEffect(() => setPersistedSelectedOfferIds(selectedOfferIds()));
    const selectedCount = () => selectedOfferIds().size;
    const allVisibleSelected = createMemo(() => currentOfferList().length > 0 && currentOfferList().every(o => selectedOfferIds().has(o.id)));

    const clearOfferSelection = () => setSelectedOfferIds(new Set<string>());

    const toggleOfferSelection = (offerId: string) => setSelectedOfferIds(prev => {
        const next = new Set(prev);
        if (next.has(offerId)) next.delete(offerId); else next.add(offerId);
        return next;
    });

    const toggleSelectAllVisible = () => {
        const ids = currentOfferList().map(o => o.id);
        const shouldSelectAll = !allVisibleSelected();
        setSelectedOfferIds(prev => {
            const next = new Set(prev);
            for (const id of ids) { if (shouldSelectAll) next.add(id); else next.delete(id); }
            return next;
        });
    };

    const deleteSelectedOffers = async () => {
        const id = botId();
        if (!id) return;
        const ids = [...selectedOfferIds()];
        if (ids.length === 0) return;
        if (!await showConfirm(t('SELLBOT_BULK_DELETE_CONFIRM', ids.length), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        let failed = 0;
        for (const offerId of ids) {
            const res = await api.sellbot.deleteOffer(id, offerId);
            if (!isOk(res.data)) failed++;
        }
        clearOfferSelection();
        await loadAll();
        if (failed > 0) toast?.error(t('SELLBOT_TOAST_BULK_DELETE_PARTIAL', failed, ids.length));
        else toast?.info(t('SELLBOT_TOAST_BULK_DELETE_SUCCESS', ids.length));
    };

    const [bulkMoveMenu, setBulkMoveMenu] = createSignal<{ x: number; y: number } | null>(null);
    let bulkMoveMenuRef: HTMLDivElement | undefined;
    const bulkMoveTargets = createMemo(() => ({
        root: currentFolderId() !== null,
        folders: allFoldersFlat().filter(({ folder }) => folder.id !== currentFolderId()),
    }));

    const openBulkMoveMenu = (e: MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setBulkMoveMenu({ x: rect.left, y: rect.bottom + 4 });
    };

    useClampToViewport(() => bulkMoveMenuRef, () => bulkMoveMenu());
    useDismissableMenu(() => bulkMoveMenu() !== null, () => setBulkMoveMenu(null));

    const moveSelectedOffersTo = async (destinationFolderId: string | null) => {
        setBulkMoveMenu(null);
        const ids = [...selectedOfferIds()];
        if (ids.length === 0) return;
        const originalFolders = new Map(ids.map(offerId =>
            [offerId, (config()?.offers ?? []).find(o => o.id === offerId)?.folderId ?? null] as const));
        for (const offerId of ids) {
            await moveTreeItem('Offer', offerId, destinationFolderId, true, { silent: true });
        }
        clearOfferSelection();
        toast?.info(t('SELLBOT_TOAST_BULK_MOVED', ids.length, folderName(destinationFolderId)), {
            actionLabel: t('COMMON_UNDO'),
            onAction: () => void (async () => {
                for (const [offerId, originalFolderId] of originalFolders) {
                    await moveTreeItem('Offer', offerId, originalFolderId, true, { silent: true });
                }
            })(),
        });
    };

    const createFolder = async () => {
        const id = botId();
        if (!id) return;
        const result = await showDialog({
            title: t('SELLBOT_FOLDER_NEW'),
            fields: [{ key: 'name', label: t('SELLBOT_FOLDER_NAME_LABEL') }],
        });
        if (!result?.name?.trim()) return;
        const res = await api.sellbot.createOfferFolder(id, { name: result.name.trim(), parentFolderId: currentFolderId() });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FOLDER_ADD_FAILED'));
    };

    const renameFolder = async (folder: SellbotOfferFolderDto) => {
        const id = botId();
        if (!id) return;
        const result = await showDialog({
            title: t('SELLBOT_FOLDER_RENAME'),
            fields: [{ key: 'name', label: t('SELLBOT_FOLDER_NAME_LABEL'), defaultValue: folder.name }],
        });
        if (!result?.name?.trim()) return;
        const res = await api.sellbot.renameOfferFolder(id, folder.id, { name: result.name.trim() });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FOLDER_RENAME_FAILED'));
    };

    const folderElementCount = (folder: SellbotOfferFolderDto) =>
        (config()?.offers ?? []).filter(o => o.folderId === folder.id).length
        + (config()?.folders ?? []).filter(f => f.parentFolderId === folder.id).length;

    const folderIsEmpty = (folder: SellbotOfferFolderDto) => folderElementCount(folder) === 0;

    const deleteFolder = async (folder: SellbotOfferFolderDto) => {
        const id = botId();
        if (!id || !folderIsEmpty(folder)) return;
        if (!await showConfirm(t('SELLBOT_FOLDER_DELETE_CONFIRM'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteOfferFolder(id, folder.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FOLDER_DELETE_FAILED'));
    };

    const [pickQuery, setPickQuery] = createSignal('');
    const [picked, setPicked] = createSignal<ItemStackDto | null>(null);
    const [submitting, setSubmitting] = createSignal(false);
    const [busyExport, setBusyExport] = createSignal(false);
    const [importing, setImporting] = createSignal(false);
    const [pendingImport, setPendingImport] = createSignal<File | null>(null);
    const [importPreview, setImportPreview] = createSignal<SellbotOfferImportResultDto | null>(null);
    let importInput: HTMLInputElement | undefined;

    const loadStock = async (id: string) => {
        const res = await api.sellbot.getChamberStock(id);
        if (isOk(res.data)) {
            setStock(reconcile(res.data.value.stacks ?? [], { key: 'key' }));
            setStockQuantities(reconcile(res.data.value.quantities ?? {}));
            return;
        }
        setStock([]);
        setStockQuantities(reconcile({}));
        toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_STOCK_FAILED'));
    };

    const loadAll = async () => {
        const id = botId();
        if (!id) { setConfig(null); setLoading(false); return; }
        const [cfg, custom] = await Promise.all([
            api.sellbot.getConfig(id),
            api.inventory.getCustomItems(),
        ]);
        const cfgVal = isOk(cfg.data) ? cfg.data.value : null;
        setConfig(cfgVal);
        if (isOk(custom.data)) {
            const items = custom.data.value.items ?? [];
            setCustomItems(reconcile(items, { key: 'hash' }));
            resolver()?.ingestCustomItems(items);
        }
        if (cfgVal) await loadStock(id);
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void loadAll(); });
        if (!id) return;
        const reloadOnEvent = debounce(() => void loadAll(), 500);
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type !== 'plugin_event' || evt.payload?.source !== 'sellbot') return;
            reloadOnEvent();
        });
        onCleanup(() => { reloadOnEvent.cancel(); unsub(); });
    });

    const onWindowScroll = () => { persistedScrollY = window.scrollY; };
    onMount(() => {
        window.addEventListener('scroll', onWindowScroll, { passive: true });
    });
    onCleanup(() => window.removeEventListener('scroll', onWindowScroll));

    createEffect(() => {
        if (loading() || !config()) return;
        const y = persistedScrollY;
        requestAnimationFrame(() => window.scrollTo(0, y));
    });

    const exportOffers = async () => {
        const id = botId();
        if (!id) return;
        setBusyExport(true);
        const failed = await downloadFromApi(getSellbotExportOffersUrl(id), `sellbot-offers-${id}.xlsx`);
        setBusyExport(false);
        if (failed) toast?.error(t('SELLBOT_OFFERS_EXPORT_FAILED'));
    };

    const runImport = async (dryRun: boolean) => {
        const id = botId();
        const file = pendingImport();
        if (!id || !file) return;
        setImporting(true);
        try {
            const res = await api.sellbot.importOffers(id, { file, dryRun });
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('SELLBOT_OFFERS_IMPORT_FAILED'));
                closeImport();
                return;
            }
            const result = res.data.value;
            if (result.applied) {
                closeImport();
                toast?.info(t('SELLBOT_OFFERS_IMPORT_APPLIED'));
                await loadAll();
                return;
            }
            setImportPreview(result);
        } finally {
            setImporting(false);
        }
    };

    const applyImport = async () => {
        if (!await showConfirm(t('SELLBOT_OFFERS_IMPORT_CONFIRM'), true,
            { confirmLabel: t('SELLBOT_OFFERS_IMPORT_APPLY'), cancelLabel: t('COMMON_CANCEL') })) return;
        await runImport(false);
    };

    const closeImport = () => { setImportPreview(null); setPendingImport(null); };

    const importIssues = () => importPreview()?.issues ?? [];

    const scanStock = async () => {
        const id = botId();
        if (!id) return;
        setBusyScan(true);
        const res = await api.sellbot.scanStock(id);
        if (isOk(res.data)) toast?.info(t('SELLBOT_TOAST_SCAN_STOCK_QUEUED'));
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_SCAN_STOCK_FAILED'));
        setBusyScan(false);
    };

    const sellbotNameCache = createSellbotNameCache();

    const searchItems = (query: string): ItemStackDto[] =>
        searchSellbotItems(query, stock, customItems, resolver(), displayName, sellbotNameCache);
    const debouncedPickQuery = useDebounced(pickQuery, 200);
    const searchResults = createMemo(() => searchItems(debouncedPickQuery()));

    const addOffer = async () => {
        const id = botId();
        const s = picked();
        if (!id || !s) return;
        setSubmitting(true);
        try {
            const res = await api.sellbot.createOffer(id, { wizard: { items: [s], pinned: [] }, folderId: currentFolderId() });
            if (isOk(res.data)) {
                // The backend derives the match rules for a custom item as part of creating the offer.
                toast?.info(t(s.isCustom && s.snbt ? 'SELLBOT_TOAST_OFFER_ADDED_RULES' : 'SELLBOT_TOAST_OFFER_ADDED'));
                setPicked(null); setPickQuery('');
                await loadAll();
            } else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_OFFER_ADD_FAILED'));
        } catch {
            toast?.error(t('SELLBOT_TOAST_OFFER_ADD_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const wizardSource: WizardItemSource = {
        placeholderKey: 'SELLBOT_PICK_PLACEHOLDER_SEARCH',
        search: searchItems,
        stockOf: (item) => stockQuantities[item.key],
    };

    const wizardDiff = async (request: SellbotWizardRequest) => {
        const id = botId();
        if (!id) return { ok: false as const, error: t('SELLBOT_TOAST_OFFER_ADD_FAILED') };
        const res = await api.sellbot.wizardDiff(id, request);
        return isOk(res.data)
            ? { ok: true as const, value: res.data.value! }
            : { ok: false as const, error: firstError(res.data) ?? t('SELLBOT_TOAST_OFFER_ADD_FAILED') };
    };

    const wizardFinish = async (request: SellbotWizardRequest) => {
        const id = botId();
        if (!id) return { ok: false };
        const created = await api.sellbot.createOffer(id, { wizard: request, folderId: currentFolderId() });
        if (!isOk(created.data)) return { ok: false, error: firstError(created.data) ?? t('SELLBOT_TOAST_OFFER_ADD_FAILED'), collision: created.data?.collision };
        toast?.info(t('SELLBOT_TOAST_OFFER_ADDED_RULES'));
        await loadAll();
        return { ok: true };
    };

    const deleteOffer = async (o: SellbotOfferDto) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('SELLBOT_CONFIRM_DELETE_OFFER'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteOffer(id, o.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(t('SELLBOT_TOAST_DELETE_FAILED'));
    };

    const toView = (s: ItemStackDto) => ({ itemId: s.itemId, count: s.count, damage: s.metadata, snbt: s.snbt ?? undefined });

    const FILTER_PILLS = [
        { value: 'all' as OfferFilter, labelKey: 'SELLBOT_FILTER_ALL' },
        { value: 'sell' as OfferFilter, labelKey: 'SELLBOT_FILTER_SELL' },
        { value: 'ankauf' as OfferFilter, labelKey: 'SELLBOT_FILTER_ANKAUF' },
        { value: 'oos' as OfferFilter, labelKey: 'SELLBOT_FILTER_OOS' },
        { value: 'rules' as OfferFilter, labelKey: 'SELLBOT_FILTER_RULES' },
    ];

    return (
        <div class={styles.container}>
            <div class={`${styles.dragScrollZone} ${styles.dragScrollZoneTop} ${dragScrollZone() === 'top' ? styles.dragScrollZoneActive : ''}`}>
                <i class="fa-solid fa-angles-up" />
            </div>
            <div class={`${styles.dragScrollZone} ${styles.dragScrollZoneBottom} ${dragScrollZone() === 'bottom' ? styles.dragScrollZoneActive : ''}`}>
                <i class="fa-solid fa-angles-down" />
            </div>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_OFFERS_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_OFFERS_SUBTITLE" /></span>
                </div>
                <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                    <button type="button" class="btn btn-sm btn-secondary" disabled={busyExport() || !config()}
                        title={t('SELLBOT_OFFERS_EXPORT_HELP')} onClick={() => void exportOffers()}>
                        <Show when={busyExport()} fallback={<><i class="fa-solid fa-file-arrow-down" /> <Text val="SELLBOT_OFFERS_EXPORT" /></>}>
                            <i class="fa-solid fa-spinner fa-spin" />
                        </Show>
                    </button>
                    <button type="button" class="btn btn-sm btn-secondary" disabled={importing() || !config()}
                        title={t('SELLBOT_OFFERS_IMPORT_HELP')} onClick={() => importInput?.click()}>
                        <i class="fa-solid fa-file-arrow-up" /> <Text val="SELLBOT_OFFERS_IMPORT" />
                    </button>
                    <input ref={importInput} type="file" accept=".xlsx" style={{ display: 'none' }}
                        onChange={e => {
                            const file = e.currentTarget.files?.[0];
                            e.currentTarget.value = '';
                            if (!file) return;
                            setPendingImport(file);
                            void runImport(true);
                        }} />
                </div>
            </div>

            <Modal open={!!importPreview()} onClose={closeImport} busy={importing()} maxWidth="44rem"
                title={<Text val="SELLBOT_OFFERS_IMPORT_PREVIEW_TITLE" />}
                subtitle={<Text val="SELLBOT_OFFERS_IMPORT_PREVIEW_HELP" />}>
                <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                    <Show when={importPreview()?.copiedFromOtherBot}>
                        <span class={styles.helper}>
                            <i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_OFFERS_IMPORT_COPY" />
                        </span>
                    </Show>
                    <table class="sessiontable">
                        <thead>
                            <tr>
                                <th><Text val="SELLBOT_OFFERS_IMPORT_ENTITY" /></th>
                                <th><Text val="SELLBOT_OFFERS_IMPORT_CREATED" /></th>
                                <th><Text val="SELLBOT_OFFERS_IMPORT_UPDATED" /></th>
                                <th><Text val="SELLBOT_OFFERS_IMPORT_DELETED" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={importPreview()?.counts ?? []}>{count => (
                                <tr>
                                    <td><Text val={'SELLBOT_OFFERS_IMPORT_ENTITY_' + count.entity.toUpperCase()} /></td>
                                    <td>{count.created}</td>
                                    <td>{count.updated}</td>
                                    <td>{count.deleted}</td>
                                </tr>
                            )}</For>
                        </tbody>
                    </table>

                    <Show when={importIssues().length > 0}>
                        <div class="v" style={{ gap: 'var(--spacing-xs)', 'max-height': '18rem', 'overflow-y': 'auto' }}>
                            <span class={styles.helper}>
                                <i class="fa-solid fa-triangle-exclamation" /> <Text val="SELLBOT_OFFERS_IMPORT_ISSUES" />
                            </span>
                            <For each={importIssues()}>{issue => (
                                <div class={styles.helper}>
                                    <span class="commandtext">{issue.sheet}!{issue.cell}</span> {issue.message}
                                </div>
                            )}</For>
                        </div>
                    </Show>

                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" onClick={closeImport}>
                            <Text val="COMMON_CANCEL" />
                        </button>
                        <button type="button" class="btn btn-danger" disabled={importing() || importIssues().length > 0}
                            onClick={() => void applyImport()}>
                            <Text val="SELLBOT_OFFERS_IMPORT_APPLY" />
                        </button>
                    </div>
                </div>
            </Modal>

            <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                <Show when={config()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-store-slash" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                        <p><Text val="SELLBOT_NOT_CONFIGURED" /></p>
                        <A href="/sellbot/config" class="btn btn-primary"><Text val="SELLBOT_SETTINGS_NAV" /></A>
                    </div>
                }>
                    <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                        <div class={styles.offersToolbar}>
                            <span class={styles.helper}><Text val="SELLBOT_OFFERS_HELP" /></span>
                            <div class={styles.offersToolbarActions}>
                                <span class={styles.helper}><span class={`${styles.badge} ${styles.badgeVerkauf}`}><i class="fa-solid fa-hand-holding-dollar" /></span> <Text val="SELLBOT_FILTER_SELL" /></span>
                                <span class={styles.helper}><span class={`${styles.badge} ${styles.badgeAnkauf}`}><i class="fa-solid fa-cart-shopping" /></span> <Text val="SELLBOT_FILTER_ANKAUF" /></span>
                                <button type="button" class="btn btn-sm btn-secondary" disabled={busyScan()} title={t('SELLBOT_SCAN_STOCK_HELP')} onClick={() => void scanStock()}>
                                    <Show when={busyScan()} fallback={<><i class="fa-solid fa-boxes-stacked" /> <Text val="SELLBOT_SCAN_STOCK" /></>}>
                                        <i class="fa-solid fa-spinner fa-spin" />
                                    </Show>
                                </button>
                            </div>
                        </div>

                        <div class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'justify-content': 'space-between', 'flex-wrap': 'wrap' }}>
                            <nav class={styles.breadcrumb}>
                                <button type="button" data-crumb-id="root"
                                    class={`${styles.breadcrumbLink} ${isCrumbDropTarget(null) ? styles.breadcrumbOver : ''}`}
                                    onClick={() => setCurrentFolderId(null)}>
                                    <i class="fa-solid fa-house" /> <Text val="SELLBOT_BREADCRUMB_ROOT" />
                                </button>
                                <For each={breadcrumb()}>{(folder) => (
                                    <>
                                        <span>/</span>
                                        <Show when={folder.id !== currentFolderId()} fallback={<span class={styles.breadcrumbCurrent}>{folder.name}</span>}>
                                            <button type="button" data-crumb-id={folder.id}
                                                class={`${styles.breadcrumbLink} ${isCrumbDropTarget(folder.id) ? styles.breadcrumbOver : ''}`}
                                                onClick={() => setCurrentFolderId(folder.id)}>
                                                {folder.name}
                                            </button>
                                        </Show>
                                    </>
                                )}</For>
                            </nav>
                            <button type="button" class="btn btn-sm btn-secondary" onClick={() => void createFolder()}>
                                <i class="fa-solid fa-folder-plus" /> <Text val="SELLBOT_FOLDER_NEW" />
                            </button>
                        </div>

                        <form class={styles.addRow} onSubmit={(e) => { e.preventDefault(); void addOffer(); }}>
                            <div class={styles.picker}>
                                <input type="text" aria-label={t('SELLBOT_ITEM')}
                                    placeholder={stock.length === 0 ? t('SELLBOT_PICK_PLACEHOLDER_EMPTY') : t('SELLBOT_PICK_PLACEHOLDER_SEARCH')}
                                    disabled={stock.length === 0}
                                    value={pickQuery()}
                                    onInput={(e) => { setPickQuery(e.currentTarget.value); setPicked(null); }} />
                                <Show when={picked()}>
                                    <div class={styles.pickedPreview}><ItemStackView stack={toView(picked()!)} /></div>
                                </Show>
                                <Show when={pickQuery() && !picked()}>
                                    <div class={styles.pickerList}>
                                        <Show when={searchResults().length > 0} fallback={<div class={styles.pickerEmpty}><Text val={pickQuery().trim() ? 'SELLBOT_PICKER_NO_MATCH' : 'SELLBOT_PICKER_TYPE_TO_SEARCH'} /></div>}>
                                            <For each={searchResults()}>{(s) => (
                                                <button type="button" class={styles.pickerItem} onClick={() => { setPicked(s); setPickQuery(displayName(s)); }}>
                                                    <ItemStackView stack={toView(s)} showTooltip />
                                                    <span>{coloredDisplayName(s)}</span>
                                                    <Show when={s.isCustom}><span class={`${styles.badge} ${styles.badgeInfo}`}><Text val="SELLBOT_OFFER_RULES" /></span></Show>
                                                </button>
                                            )}</For>
                                        </Show>
                                    </div>
                                </Show>
                            </div>
                            <button type="submit" class="btn btn-primary" disabled={submitting() || !picked()}>
                                <Show when={submitting()} fallback={<><i class="fa-solid fa-plus" /> <Text val="SELLBOT_ADD" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                            <button type="button" class="btn btn-secondary" disabled={stock.length === 0} onClick={() => setWizardOpen(true)}>
                                <i class="fa-solid fa-wand-magic-sparkles" /> <Text val="SELLBOT_OFFERS_WIZARD_START" />
                            </button>
                        </form>

                        <ItemIdentityWizard open={wizardOpen()} onClose={() => setWizardOpen(false)}
                            source={wizardSource} diff={wizardDiff} onFinish={wizardFinish}
                            collisionHref={(entityId) => `/sellbot/offer/${entityId}`} />

                        <div class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                            <div class={styles.picker} style={{ flex: '1', 'min-width': '12rem' }}>
                                <input type="text" placeholder={t('SELLBOT_OFFERS_SEARCH_PLACEHOLDER')} value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
                            </div>
                            <DateRangePills value={filter()} onChange={setFilter} pills={FILTER_PILLS} />
                        </div>

                        <Show when={isSearching()
                            ? filteredOffers().length > 0
                            : (visibleFolders().length + visibleOffers().length) > 0 || currentFolderId() !== null} fallback={
                            <div class={styles.emptySmall}><i class="fa-solid fa-tags" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val={(config()?.offers.length ?? 0) === 0 ? 'SELLBOT_NO_OFFERS' : 'SELLBOT_OFFERS_NO_MATCH'} /></p></div>
                        }>
                            <BulkActionBar
                                selectedCount={selectedCount()}
                                matchingCount={currentOfferList().length}
                                allMatchingSelected={allVisibleSelected()}
                                onSelectAllMatching={toggleSelectAllVisible}
                                onClear={clearOfferSelection}
                                actions={
                                    <>
                                        <button type="button" class="btn btn-secondary btn-sm" onClick={openBulkMoveMenu}>
                                            <i class="fa-solid fa-folder-tree" /> <Text val="SELLBOT_BULK_MOVE" />
                                        </button>
                                        <button type="button" class="btn btn-danger btn-sm" onClick={() => void deleteSelectedOffers()}>
                                            <i class="fa-solid fa-trash" /> <Text val="COMMON_DELETE" />
                                        </button>
                                    </>
                                }
                            />
                            <div class={styles.tableScroll}>
                                <table class="sessiontable">
                                    <thead>
                                        <tr>
                                            <th class={styles.thSelect}>
                                                <input type="checkbox" aria-label={t('SELLBOT_SELECT_ALL')}
                                                    checked={allVisibleSelected()} onChange={toggleSelectAllVisible} />
                                            </th>
                                            <Show when={!isSearching()}><th /></Show>
                                            <th />
                                            <th class={styles.colItemName}><Text val="SELLBOT_ITEM" /></th>
                                            <th><Text val="SELLBOT_FILTER_SELL" /></th>
                                            <th><Text val="SELLBOT_FILTER_ANKAUF" /></th>
                                            <th class={styles.colStock}><Text val="SELLBOT_STOCK" /></th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <Show when={!isSearching()}>
                                            <Show when={currentFolderId() !== null}>
                                                <tr data-uprow class={`${styles.treeRow} ${drag()?.mode?.kind === 'up' ? styles.treeRowOverFolder : ''}`}>
                                                    <td />
                                                    <td />
                                                    <td colSpan={2}>
                                                        <button type="button" class={styles.folderName} onClick={() => setCurrentFolderId(parentFolderId())}>
                                                            <i class="fa-solid fa-arrow-turn-up" /> <Text val="SELLBOT_FOLDER_UP" />
                                                        </button>
                                                    </td>
                                                    <td /><td /><td /><td />
                                                </tr>
                                            </Show>
                                            <For each={visibleFolders()}>{(folder, folderIndex) => (
                                                <tr data-tree-row-id={folder.id} data-tree-row-type="Folder"
                                                    class={`${styles.treeRow} ${isFolderEnterTarget(folder.id) ? styles.treeRowOverFolder : ''}`}
                                                    style={{
                                                        transform: `translateY(${rowShift(folderIndex())}px)`,
                                                        transition: 'transform 120ms ease',
                                                        opacity: drag()?.type === 'Folder' && drag()?.id === folder.id ? 0 : 1,
                                                    }}
                                                    onContextMenu={openContextMenu('Folder', folder.id)}>
                                                    <td />
                                                    <td>
                                                        <i class={`fa-solid fa-grip-vertical ${styles.dragHandle}`}
                                                            onPointerDown={isSearching() ? undefined : beginDrag('Folder', folder.id, folder.name, 'fa-folder')} />
                                                    </td>
                                                    <td colSpan={5}>
                                                        <button type="button" class={styles.folderName} onClick={() => setCurrentFolderId(folder.id)}>
                                                            <i class={`fa-solid ${isFolderEnterTarget(folder.id) ? 'fa-folder-open' : 'fa-folder'}`} />
                                                            <span class={styles.folderNameText}>{folder.name}</span>
                                                            <span class={`${styles.helper} ${styles.folderElementCount}`}>({t('SELLBOT_FOLDER_ELEMENT_COUNT', folderElementCount(folder))})</span>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                                            <button type="button" class="btn btn-sm btn-secondary" aria-label={t('SELLBOT_FOLDER_RENAME')} title={t('SELLBOT_FOLDER_RENAME')} onClick={() => void renameFolder(folder)}><i class="fa-solid fa-pen-to-square" /></button>
                                                            <button type="button" class="btn btn-sm btn-danger" disabled={!folderIsEmpty(folder)}
                                                                aria-label={t('SELLBOT_FOLDER_DELETE')}
                                                                title={folderIsEmpty(folder) ? t('SELLBOT_FOLDER_DELETE') : t('SELLBOT_FOLDER_NOT_EMPTY')}
                                                                onClick={() => void deleteFolder(folder)}><i class="fa-solid fa-trash" /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}</For>
                                            <Show when={visibleFolders().length + visibleOffers().length === 0}>
                                                <tr class={styles.treeRow}>
                                                    <td /><td /><td colSpan={6}><span class={styles.helper}><Text val="SELLBOT_FOLDER_EMPTY" /></span></td>
                                                </tr>
                                            </Show>
                                        </Show>
                                        <For each={isSearching() ? filteredOffers() : visibleOffers()}>{(o, index) => (
                                            <tr data-tree-row-id={isSearching() ? undefined : o.id} data-tree-row-type={isSearching() ? undefined : 'Offer'}
                                                class={styles.treeRow}
                                                style={isSearching() ? undefined : {
                                                    transform: `translateY(${rowShift(visibleFolders().length + index())}px)`,
                                                    transition: 'transform 120ms ease',
                                                    // The dragged row's own table row keeps its layout space (removing it would
                                                    // reflow every row below and break the translateY math above), so hide it
                                                    // instead - otherwise a neighbor sliding into that space renders on top of
                                                    // this row's still-visible content instead of into an empty gap.
                                                    opacity: drag()?.type === 'Offer' && drag()?.id === o.id ? 0 : 1,
                                                }}
                                                onContextMenu={openContextMenu('Offer', o.id)}>
                                                <td>
                                                    <input type="checkbox" aria-label={displayName(o.item)}
                                                        checked={selectedOfferIds().has(o.id)} onChange={() => toggleOfferSelection(o.id)} />
                                                </td>
                                                <Show when={!isSearching()}>
                                                    <td>
                                                        <i class={`fa-solid fa-grip-vertical ${styles.dragHandle}`}
                                                            onPointerDown={beginDrag('Offer', o.id, displayName(o.item), 'fa-cube')} />
                                                    </td>
                                                </Show>
                                                <td><ItemStackView stack={toView(o.item)} showTooltip /></td>
                                                <td class={styles.colItemName}>
                                                    <div class="v" style={{ gap: '2px' }}>
                                                        <div class={styles.badgeRow}>
                                                            <span>{displayName(o.item)}</span>
                                                            <span class={`${styles.badge} ${releasedCount(o) > 0 ? styles.badgeInfo : styles.badgeMuted}`}>{releasedCount(o) > 0 ? t('SELLBOT_OFFER_RELEASED', releasedCount(o)) : t('SELLBOT_OFFER_EXACT')}</span>
                                                        </div>
                                                        <Show when={o.note}><span class={styles.helper}>{o.note}</span></Show>
                                                        <Show when={isSearching() && o.folderId}>
                                                            <button type="button" class={styles.searchFolderTag}
                                                                onClick={() => { setSearch(''); setFilter('all'); setCurrentFolderId(o.folderId ?? null); }}>
                                                                <i class="fa-solid fa-folder" /> {t('SELLBOT_SEARCH_IN_FOLDER', folderName(o.folderId ?? null))}
                                                            </button>
                                                        </Show>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div class={styles.badgeRow}>
                                                        <Show when={o.tiers.length > 0}>
                                                            <span class={`${styles.badge} ${styles.badgeVerkauf}`} title={t('SELLBOT_FILTER_SELL')}><i class="fa-solid fa-hand-holding-dollar" /> {tierSummary(o)}</span>
                                                        </Show>
                                                        <Show when={hasVerkaufLimit(o)}>
                                                            <i class={`fa-solid fa-gauge-high ${styles.limitIcon}`} title={t('SELLBOT_LIMIT_ACTIVE_VERKAUF')} />
                                                        </Show>
                                                        <Show when={hasPausedTier(o)}><SellbotPausedBadge /></Show>
                                                        <For each={bundlesOf(o)}>{(bundle) => (
                                                            <span class={`${styles.badge} ${styles.badgeBundle}`} title={t('SELLBOT_BUNDLE_HELP')}><i class="fa-solid fa-box-open" /> {bundle.name ?? t('SELLBOT_BUNDLE_BADGE')}</span>
                                                        )}</For>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div class={styles.badgeRow}>
                                                        <Show when={o.ankaufTier}>
                                                            <span class={`${styles.badge} ${styles.badgeAnkauf}`} title={t('SELLBOT_FILTER_ANKAUF')}><i class="fa-solid fa-cart-shopping" /> {ankaufSummary(o)}</span>
                                                        </Show>
                                                        <Show when={hasAnkaufLimit(o)}>
                                                            <i class={`fa-solid fa-gauge-high ${styles.limitIcon}`} title={t('SELLBOT_LIMIT_ACTIVE_ANKAUF')} />
                                                        </Show>
                                                        <Show when={hasPausedAnkaufStage(o)}><SellbotPausedBadge /></Show>
                                                    </div>
                                                </td>
                                                <td class={styles.colStock}>
                                                    <span class={stockClass(o)} title={o.ignoresStockCache ? t('SELLBOT_OFFER_STOCK_FROM_LAST_SCAN_HELP') : undefined}>
                                                        {formatQuantity(o.availableStock, offerStackSize(o))}<Show when={o.ignoresStockCache}> <i class="fa-solid fa-eye-slash" /></Show>
                                                    </span>
                                                </td>
                                                <td>
                                                    <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                                        <A href={`/sellbot/offer/${o.id}`} class="btn btn-sm btn-secondary"><i class="fa-solid fa-sliders" /> <Text val="SELLBOT_DETAILS" /></A>
                                                        <button type="button" class="btn btn-sm btn-danger" aria-label={t('SELLBOT_DELETE_OFFER')} title={t('SELLBOT_DELETE_OFFER')} onClick={() => void deleteOffer(o)}><i class="fa-solid fa-trash" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}</For>
                                    </tbody>
                                </table>
                            </div>
                        </Show>
                    </div>
                </Show>
            </Show>

            <Show when={contextMenu()}>
                {(menu) => (
                    <Portal mount={document.body}>
                        <div ref={contextMenuRef} class={styles.contextMenu} style={{ left: `${menu().x}px`, top: `${menu().y}px` }}
                            onClick={(e) => e.stopPropagation()}>
                            <span class={styles.contextMenuHeader}><Text val="SELLBOT_CONTEXT_MOVE_TO" /></span>
                            <Show when={moveTargetsFor(menu().itemType, menu().itemId).root}>
                                <button type="button" class={styles.contextMenuItem} onClick={() => moveViaMenu(menu().itemType, menu().itemId, null)}>
                                    <i class="fa-solid fa-house" /> <Text val="SELLBOT_BREADCRUMB_ROOT" />
                                </button>
                            </Show>
                            <For each={moveTargetsFor(menu().itemType, menu().itemId).folders}>{({ folder, depth }) => (
                                <button type="button" class={styles.contextMenuItem}
                                    style={{ 'padding-left': `calc(var(--spacing-sm) + ${depth} * var(--spacing-lg))` }}
                                    onClick={() => moveViaMenu(menu().itemType, menu().itemId, folder.id)}>
                                    <i class="fa-solid fa-folder" /> {folder.name}
                                </button>
                            )}</For>
                            <Show when={!moveTargetsFor(menu().itemType, menu().itemId).root && moveTargetsFor(menu().itemType, menu().itemId).folders.length === 0}>
                                <span class={styles.pickerEmpty}><Text val="SELLBOT_CONTEXT_MOVE_NO_TARGETS" /></span>
                            </Show>
                        </div>
                    </Portal>
                )}
            </Show>

            <Show when={bulkMoveMenu()}>
                {(menu) => (
                    <Portal mount={document.body}>
                        <div ref={bulkMoveMenuRef} class={styles.contextMenu} style={{ left: `${menu().x}px`, top: `${menu().y}px` }}
                            onClick={(e) => e.stopPropagation()}>
                            <span class={styles.contextMenuHeader}><Text val="SELLBOT_CONTEXT_MOVE_TO" /></span>
                            <Show when={bulkMoveTargets().root}>
                                <button type="button" class={styles.contextMenuItem} onClick={() => void moveSelectedOffersTo(null)}>
                                    <i class="fa-solid fa-house" /> <Text val="SELLBOT_BREADCRUMB_ROOT" />
                                </button>
                            </Show>
                            <For each={bulkMoveTargets().folders}>{({ folder, depth }) => (
                                <button type="button" class={styles.contextMenuItem}
                                    style={{ 'padding-left': `calc(var(--spacing-sm) + ${depth} * var(--spacing-lg))` }}
                                    onClick={() => void moveSelectedOffersTo(folder.id)}>
                                    <i class="fa-solid fa-folder" /> {folder.name}
                                </button>
                            )}</For>
                            <Show when={!bulkMoveTargets().root && bulkMoveTargets().folders.length === 0}>
                                <span class={styles.pickerEmpty}><Text val="SELLBOT_CONTEXT_MOVE_NO_TARGETS" /></span>
                            </Show>
                        </div>
                    </Portal>
                )}
            </Show>

            <Show when={drag()}>
                {(d) => (
                    <Portal mount={document.body}>
                        <div class={styles.dragGhost} style={{
                            left: `${d().pointerX - d().grabOffsetX}px`,
                            top: `${d().pointerY - d().grabOffsetY}px`,
                        }}>
                            <i class={`fa-solid fa-grip-vertical ${styles.dragHandle}`} />
                            <i class={`fa-solid ${d().icon}`} />
                            <span>{d().label}</span>
                        </div>
                    </Portal>
                )}
            </Show>
        </div>
    );
}
